import React, { useState, useRef } from "react";
import HCaptcha from "@hcaptcha/react-hcaptcha";
import MessageBox from "./Messagebox";
import LoadingBox from "./LoadingBox";

const QuoteForm = () => {
  const [warningMessage, setWarningMessage] = useState("");
  const [images, setImages] = useState([]);
  const fileInputRef = useRef(null);
  const [name, setName] = useState("");
  const [surname, setSurname] = useState("");
  const [cell, setCell] = useState("");
  const [email, setEmail] = useState("");
  const [address, setAddress] = useState("");
  const [description, setDescription] = useState("");
  const [errors, setErrors] = useState({});
  const [message, setMessage] = useState("");
  const [isMessageOpen, setIsMessageOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const captchaRef = useRef(null);
  const [captchaToken, setCaptchaToken] = useState(null);
  const maxLength = 500;

  const handleButtonClick = (e) => {
    e.preventDefault();
    fileInputRef.current.click();
  };
  const handleClick = (index) => {
    setImages((prev) => prev.filter((_, i) => i !== index));
    setWarningMessage("");
  };
  const fileToBase64 = (file) =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result.split(",")[1]);
      reader.onerror = (error) => reject(error);
    });

  const handleFileChange = async (e) => {
    const files = Array.from(e.target.files);
    if (images.length + files.length > 3) {
      setWarningMessage("3 images is the limit");
      return;
    }
    for (const file of files) {
      if (!file.type.startsWith("image/")) {
        setWarningMessage("Only image files are allowed");
        return;
      }
      if (file.size > 2 * 1024 * 1024) {
        setWarningMessage(`"${file.name}" must be under 2MB`);
        return;
      }
    }
    try {
      const base64Images = await Promise.all(
        files.map((file) => fileToBase64(file)),
      );
      setImages((prev) => [...prev, ...base64Images].slice(0, 3));
      setWarningMessage("");
    } catch (err) {
      setWarningMessage("Failed to process images");
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    let tempErrors = {};
    if (!name.trim()) tempErrors.name = "Required";
    if (!surname.trim()) tempErrors.surname = "Required";
    if (!cell.trim()) tempErrors.cell = "Required";
    if (!email.trim()) tempErrors.email = "Required";
    if (!address.trim()) tempErrors.address = "Required";
    if (!description.trim()) tempErrors.description = "Required";
    if (!captchaToken) tempErrors.captcha = "Please complete the captcha";
    setErrors(tempErrors);
    if (Object.keys(tempErrors).length === 0) {
      setIsLoading(true);
      const quoteData = {
        quoteID: crypto.randomUUID(),
        userName: name,
        userSurname: surname,
        userCellNumber: cell,
        userEmailAddress: email,
        userAddress: address,
        userDescription: description,
        userImagesURLs: images,
        captchaToken,
      };
      try {
        const response = await fetch(
          "https://trademinwebapp-g5atgdgehba7dpbb.westus2-01.azurewebsites.net/api/Integration/requestQuote",
          {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(quoteData),
          },
        );
        const contentType = response.headers.get("content-type");
        const responseData = contentType?.includes("application/json")
          ? await response.json()
          : await response.text();
        setMessage(responseData.errorMessage || "Quote request processed");
        setIsMessageOpen(true);
        captchaRef.current?.resetCaptcha();
        if (responseData.validationResponse) {
          setName("");
          setSurname("");
          setCell("");
          setEmail("");
          setAddress("");
          setDescription("");
          setImages([]);
          setErrors({});
          setCaptchaToken(null);
        }
      } catch (error) {
        setMessage("An error occurred while submitting your quote request");
        setIsMessageOpen(true);
      } finally {
        setIsLoading(false);
      }
    }
  };

  const styles = `
    @import url('https://fonts.googleapis.com/css2?family=DM+Serif+Display:ital@0;1&family=DM+Sans:opsz,wght@9..40,300;9..40,400;9..40,500&display=swap');

    .qf-wrap {
      width: 100%;
      max-width: 100%;
      box-sizing: border-box;
    }

    .qf-row { display: grid; grid-template-columns: 1fr 1fr; gap: 1rem; margin-bottom: 1rem; }
    .qf-field { margin-bottom: 1rem; }

    .qf-label {
      display: flex; align-items: center; justify-content: space-between;
      font-size: 0.62rem; letter-spacing: 0.2em; text-transform: uppercase;
      color: #E78730; font-weight: 500; margin-bottom: 0.5rem;
    }

    .qf-error { font-size: 0.65rem; color: #c0392b; font-weight: 400; letter-spacing: 0; text-transform: none; }

    .qf-input, .qf-textarea {
      width: 100%;
      padding: 0.7rem 0.85rem;
      background: #fafaf8;
      border: 1px solid #e8e8e4;
      border-bottom: 1px solid #ccc;
      font-family: 'DM Sans', sans-serif;
      font-size: 0.875rem;
      font-weight: 300;
      color: #1a1a1a;
      outline: none;
      transition: border-color 0.2s, background 0.2s;
      box-sizing: border-box;
    }
    .qf-input::placeholder, .qf-textarea::placeholder { color: #ccc; }
    .qf-input:focus, .qf-textarea:focus { border-color: #E78730; background: #fff; }
    .qf-textarea { resize: none; height: 100px; }

    .qf-char { font-size: 0.6rem; color: #bbb; letter-spacing: 0.08em; font-weight: 400; text-transform: none; }

    .qf-upload-row { display: flex; align-items: center; justify-content: space-between; margin-bottom: 0.6rem; }

    .qf-upload-btn {
      display: inline-flex; align-items: center; gap: 0.4rem;
      border: 1px solid #e8e8e4; background: none;
      padding: 0.45rem 1rem; cursor: pointer;
      font-size: 0.65rem; letter-spacing: 0.14em; text-transform: uppercase;
      color: #aaa; font-family: 'DM Sans', sans-serif;
      transition: border-color 0.2s, color 0.2s;
    }
    .qf-upload-btn:hover { border-color: #E78730; color: #E78730; }

    .qf-warning { font-size: 0.7rem; color: #c0392b; margin-bottom: 0.5rem; display: block; }

    .qf-img-preview { display: flex; flex-wrap: wrap; gap: 0.5rem; margin-top: 0.75rem; }
    .qf-img-thumb { position: relative; width: 60px; height: 60px; border: 1px solid #e8e8e4; overflow: hidden; }
    .qf-img-thumb img { width: 100%; height: 100%; object-fit: cover; display: block; }
    .qf-img-remove { position: absolute; top: 2px; right: 2px; width: 16px; height: 16px; background: #1a1a1a; color: #fff; border: none; cursor: pointer; display: flex; align-items: center; justify-content: center; font-size: 9px; }

    .qf-divider { border: none; border-top: 1px solid #e8e8e4; margin: 1.5rem 0; }

    .qf-captcha-wrap { display: flex; flex-direction: column; align-items: center; gap: 0.4rem; margin-bottom: 1.25rem; }

    .qf-privacy { font-size: 0.7rem; color: #aaa; text-align: center; line-height: 1.7; margin-bottom: 1.25rem; font-weight: 300; }
    .qf-privacy a { color: #E78730; text-decoration: none; }
    .qf-privacy a:hover { text-decoration: underline; }

    .qf-submit {
      width: 100%; background: #E78730; color: #fff; border: none; cursor: pointer;
      padding: 0.875rem; font-size: 0.7rem; font-weight: 500;
      letter-spacing: 0.18em; text-transform: uppercase;
      font-family: 'DM Sans', sans-serif; transition: opacity 0.2s;
    }
    .qf-submit:hover:not(:disabled) { opacity: 0.82; }
    .qf-submit:disabled { background: #ddd; cursor: not-allowed; color: #aaa; }

    @media (max-width: 560px) {
      .qf-wrap {
        padding: 1.25rem;
      }
      .qf-captcha-wrap iframe {
        transform: scale(0.92);
        transform-origin: center;
      }
    }
  `;

  return (
    <>
      <style>{styles}</style>
      <form className="qf-wrap" onSubmit={handleSubmit}>
        <div className="qf-row">
          <div>
            <div className="qf-label">
              Name *{" "}
              {errors.name && <span className="qf-error">{errors.name}</span>}
            </div>
            <input
              className="qf-input"
              type="text"
              maxLength={20}
              value={name}
              onChange={(e) => {
                if (e.target.value.length <= 20) setName(e.target.value);
              }}
              placeholder="First name"
            />
          </div>
          <div>
            <div className="qf-label">
              Surname *{" "}
              {errors.surname && (
                <span className="qf-error">{errors.surname}</span>
              )}
            </div>
            <input
              className="qf-input"
              type="text"
              maxLength={20}
              value={surname}
              onChange={(e) => {
                if (e.target.value.length <= 20) setSurname(e.target.value);
              }}
              placeholder="Last name"
            />
          </div>
        </div>

        <div className="qf-row">
          <div>
            <div className="qf-label">
              Cell *{" "}
              {errors.cell && <span className="qf-error">{errors.cell}</span>}
            </div>
            <input
              className="qf-input"
              type="text"
              maxLength={15}
              value={cell}
              onChange={(e) => {
                if (e.target.value.length <= 15) setCell(e.target.value);
              }}
              placeholder="123456789"
            />
          </div>
          <div>
            <div className="qf-label">
              Email *{" "}
              {errors.email && <span className="qf-error">{errors.email}</span>}
            </div>
            <input
              className="qf-input"
              type="email"
              maxLength={35}
              value={email}
              onChange={(e) => {
                if (e.target.value.length <= 35) setEmail(e.target.value);
              }}
              placeholder="you@example.com"
            />
          </div>
        </div>

        <div className="qf-field">
          <div className="qf-label">
            Address *{" "}
            {errors.address && (
              <span className="qf-error">{errors.address}</span>
            )}
          </div>
          <input
            className="qf-input"
            type="text"
            maxLength={40}
            value={address}
            onChange={(e) => {
              if (e.target.value.length <= 40) setAddress(e.target.value);
            }}
            placeholder="Street, City"
          />
        </div>

        <div className="qf-field">
          <div className="qf-label">
            Description *{" "}
            {errors.description && (
              <span className="qf-error">{errors.description}</span>
            )}
            <span className="qf-char">
              {description.length}/{maxLength}
            </span>
          </div>
          <textarea
            className="qf-textarea"
            maxLength={500}
            value={description}
            onChange={(e) => {
              if (e.target.value.length <= 500) setDescription(e.target.value);
            }}
            placeholder="Describe your project or requirements..."
          />
        </div>

        <div className="qf-field">
          <div className="qf-upload-row">
            <div className="qf-label" style={{ margin: 0 }}>
              Images (Optional)
            </div>
            <button
              type="button"
              className="qf-upload-btn"
              onClick={handleButtonClick}
            >
              <svg
                width="11"
                height="11"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.5"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M4 16v1a2 2 0 002 2h12a2 2 0 002-2v-1M12 12V4m0 0l-4 4m4-4l4 4"
                />
              </svg>
              Choose Files
            </button>
          </div>
          {warningMessage && (
            <span className="qf-warning">{warningMessage}</span>
          )}
          <input
            type="file"
            multiple
            accept="image/*"
            ref={fileInputRef}
            onChange={handleFileChange}
            style={{ display: "none" }}
          />
          {images.length > 0 && (
            <div className="qf-img-preview">
              {images.map((image, index) => (
                <div key={index} className="qf-img-thumb">
                  <img
                    src={`data:image/jpeg;base64,${image}`}
                    alt={`Preview ${index}`}
                  />
                  <button
                    className="qf-img-remove"
                    onClick={(e) => {
                      e.preventDefault();
                      handleClick(index);
                    }}
                  >
                    ×
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        <hr className="qf-divider" />

        <div className="qf-captcha-wrap">
          {errors.captcha && (
            <span style={{ fontSize: "0.7rem", color: "#c0392b" }}>
              {errors.captcha}
            </span>
          )}
          <HCaptcha
            sitekey="c754f034-319e-488c-89b8-240c44f621e4"
            onVerify={(token) => setCaptchaToken(token)}
            onExpire={() => setCaptchaToken(null)}
            ref={captchaRef}
            theme="light"
          />
        </div>

        <p className="qf-privacy">
          By submitting, you agree to our{" "}
          <a href="/privacy-policy">Privacy Policy</a>. Your information is used
          only to respond to your request.
        </p>

        <button type="submit" className="qf-submit" disabled={isLoading}>
          {isLoading ? "Submitting..." : "Submit Request"}
        </button>
      </form>

      <MessageBox
        message={message}
        isOpen={isMessageOpen}
        onClose={() => setIsMessageOpen(false)}
      />
      <LoadingBox isOpen={isLoading} />
    </>
  );
};

export default QuoteForm;
